import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Leaf,
  Brain,
  Layers,
  Kanban,
  GraduationCap,
  Linkedin,
  FileText,
  Github,
  BookOpen,
  MapPin
} from 'lucide-react';

/** Logo React (elipses orbitais + círculo central), monocromático. */
const ReactBrandIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.15"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    <ellipse cx="12" cy="12" rx="10" ry="3.8" />
    <ellipse cx="12" cy="12" rx="10" ry="3.8" transform="rotate(60 12 12)" />
    <ellipse cx="12" cy="12" rx="10" ry="3.8" transform="rotate(120 12 12)" />
  </svg>
);

const Founder = () => {
  const { t } = useLanguage();

  const expertise = [
    { icon: Leaf, text: t.founder.expertise1 },
    { icon: ReactBrandIcon, text: t.founder.expertise2 },
    { icon: Brain, text: t.founder.expertise3 },
    { icon: Layers, text: t.founder.expertise4 },
    { icon: Kanban, text: t.founder.expertise5 },
    { icon: GraduationCap, text: t.founder.expertise6 }
  ];

  const links = [
    {
      icon: Linkedin,
      label: t.founder.linkedin,
      url: 'https://www.linkedin.com/in/william-analistadesistema/'
    },
    {
      icon: Github,
      label: t.founder.github,
      url: 'https://github.com/williamsartijose'
    },
    {
      icon: FileText,
      label: t.founder.lattes,
      url: 'http://lattes.cnpq.br/6687923420787618'
    },
    {
      icon: BookOpen,
      label: t.founder.professionalCourse,
      url: 'https://pay.hotmart.com/G102925497R?bid=1764249063596'
    }
  ];

  return (
    <section id="founder" className="py-28 sm:py-32 relative overflow-hidden w-full min-w-0">
      <div className="absolute inset-0 tech-grid opacity-20"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              {t.founder.title}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-500 to-yellow-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-16 items-start">
            <div className="space-y-9 min-w-0 lg:pr-2">
              <div className="card-premium p-7 sm:p-8 rounded-2xl">
                <div className="flex items-center gap-5 sm:gap-6 mb-7">
                  <img
                    src="/images/founder-william.png"
                    alt={t.founder.name}
                    className="w-[6.5rem] h-[6.5rem] sm:w-28 sm:h-28 rounded-2xl object-cover border border-white/18 shadow-xl shadow-black/40 ring-1 ring-white/[0.12] flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-2xl sm:text-[1.65rem] font-black text-white tracking-tight leading-tight">
                      {t.founder.name}
                    </h3>
                    <p className="text-yellow-500 font-medium mt-1.5 text-sm sm:text-base tracking-wide">
                      {t.founder.role}
                    </p>
                  </div>
                </div>

                <div className="space-y-6 mb-6 max-w-prose min-w-0">
                  <p className="text-white/65 leading-[1.72] font-light break-words">
                    {t.founder.bio}
                  </p>
                  <p className="text-white/65 leading-[1.72] font-light break-words">
                    {t.founder.bio2}
                  </p>
                </div>

                <div className="flex items-center gap-2 pt-7 border-t border-white/10">
                  <MapPin className="w-5 h-5 text-yellow-500" />
                  <span className="text-white/60 font-medium">{t.founder.location}</span>
                </div>
              </div>

              <div className="space-y-5">
                <h4 className="text-lg font-bold text-white mb-1 tracking-wide">
                  {t.founder.authorityTitle}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {links.map((link, index) => {
                    const Icon = link.icon;
                    return (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block card-premium p-4 sm:p-5 rounded-xl group transition-all duration-300 ease-out border border-white/[0.08] hover:border-yellow-500/28 hover:bg-white/[0.03] hover:shadow-[0_6px_28px_-10px_rgba(0,0,0,0.5)]"
                      >
                        <div className="flex items-center gap-3.5 sm:gap-4 transition-transform duration-300 ease-out group-hover:translate-x-0.5">
                          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-[1.03]">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <span className="text-white/78 font-medium transition-colors duration-300 group-hover:text-white">
                            {link.label}
                          </span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="min-w-0 lg:pl-1">
              <h4 className="text-2xl font-black text-white mb-7 sm:mb-8 tracking-tight">
                {t.founder.expertiseTitle}
              </h4>
              <div className="grid sm:grid-cols-2 gap-3.5 sm:gap-4 sm:auto-rows-fr">
                {expertise.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="card-premium p-5 sm:p-6 rounded-xl group h-full min-h-0 border border-white/[0.05] transition-[box-shadow,border-color] duration-300 hover:border-yellow-500/15"
                    >
                      <div className="flex items-start gap-3.5 h-full">
                        <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-[1.04]">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-white/75 font-medium leading-snug pt-0.5 break-words min-w-0">
                          {item.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founder;
